import api from './client';

/**
 * Legal Workflow API calls
 */
export const legalAPI = {
  // RSV Companies (reference data)
  /**
   * Get all active RSV companies
   */
  getRSVCompanies: async () => {
    const response = await api.get('/api/legal/rsv-companies/');
    return response.data;
  },

  // Legal Process
  /**
   * Get legal process for a case (creates if doesn't exist)
   */
  getLegalProcess: async (caseId: number) => {
    const response = await api.get(`/api/legal/cases/${caseId}/process/`);
    return response.data;
  },

  /**
   * Update legal process for a case
   */
  updateLegalProcess: async (caseId: number, processData: any) => {
    const response = await api.put(`/api/legal/cases/${caseId}/process/`, processData);
    return response.data;
  },

  // Case Actions (timeline events)
  /**
   * Get all actions for a case
   * @param {number} caseId
   * @param {object} options - { clientView: boolean }
   */
  getActions: async (caseId: number, options: any = {}) => {
    const params = options.clientView ? { client_view: 'true' } : {};
    const response = await api.get(`/api/legal/cases/${caseId}/actions/`, { params });
    return response.data;
  },

  /**
   * Create new action for a case
   */
  createAction: async (caseId: number, actionData: any) => {
    const response = await api.post(`/api/legal/cases/${caseId}/actions/`, actionData);
    return response.data;
  },

  /**
   * Get single action
   */
  getAction: async (actionId: number) => {
    const response = await api.get(`/api/legal/actions/${actionId}/`);
    return response.data;
  },

  /**
   * Update action
   */
  updateAction: async (actionId: number, actionData: any) => {
    const response = await api.put(`/api/legal/actions/${actionId}/`, actionData);
    return response.data;
  },

  /**
   * Delete action
   */
  deleteAction: async (actionId: number) => {
    const response = await api.delete(`/api/legal/actions/${actionId}/`);
    return response.data;
  },

  // Correspondence
  /**
   * Get all correspondence for a case
   * @param {number} caseId
   * @param {object} options - { partyType, direction, clientView }
   */
  getCorrespondences: async (caseId: number, options: any = {}) => {
    const params: any = {};
    if (options.partyType) params.party_type = options.partyType;
    if (options.direction) params.direction = options.direction;
    if (options.clientView) params.client_view = 'true';
    const response = await api.get(`/api/legal/cases/${caseId}/correspondences/`, { params });
    return response.data;
  },

  /**
   * Create new correspondence
   */
  createCorrespondence: async (caseId: number, correspondenceData: any) => {
    const response = await api.post(`/api/legal/cases/${caseId}/correspondences/`, correspondenceData);
    return response.data;
  },

  /**
   * Get single correspondence
   */
  getCorrespondence: async (correspondenceId: number) => {
    const response = await api.get(`/api/legal/correspondences/${correspondenceId}/`);
    return response.data;
  },

  /**
   * Update correspondence
   */
  updateCorrespondence: async (correspondenceId: number, correspondenceData: any) => {
    const response = await api.put(`/api/legal/correspondences/${correspondenceId}/`, correspondenceData);
    return response.data;
  },

  /**
   * Delete correspondence
   */
  deleteCorrespondence: async (correspondenceId: number) => {
    const response = await api.delete(`/api/legal/correspondences/${correspondenceId}/`);
    return response.data;
  },

  /**
   * Mark correspondence as sent (mock)
   */
  sendCorrespondence: async (correspondenceId: number) => {
    const response = await api.post(`/api/legal/correspondences/${correspondenceId}/send/`);
    return response.data;
  },

  // Timeline
  /**
   * Get combined timeline for a case (actions + correspondences)
   */
  getTimeline: async (caseId: number) => {
    const response = await api.get(`/api/legal/cases/${caseId}/timeline/`);
    return response.data;
  },

  /**
   * Get client-visible timeline
   */
  getClientTimeline: async (caseId: number) => {
    const response = await api.get(`/api/legal/client/cases/${caseId}/timeline/`);
    return response.data;
  },

  // Document Templates
  /**
   * Get all active document templates
   */
  getTemplates: async () => {
    const response = await api.get('/api/legal/templates/');
    return response.data;
  },

  /**
   * Generate document from template
   * @param {number} caseId - The case ID
   * @param {string} templateType - Template type (e.g., 'mahnschreiben', 'klage', 'rsv_request')
   * @returns {Promise<{status: string, content_json: object, template: object, output_format: string, requires_review: boolean}>}
   */
  generateDocument: async (caseId: number, templateType: string) => {
    const response = await api.post(`/api/legal/cases/${caseId}/generate/`, {
      template_type: templateType,
    });
    return response.data;
  },
};
